import { defaultTheme } from '@dealstack/frontend/shared/mechanism/styleguide/defaultTheme';
import { DS_AI_COLORS, DS_AI_POP_FROM, dsAiScale, dsAiTravel } from './dealstackAi.tokens';

/**
 * Animation names, not keyframes objects.
 *
 * The feature styles through two engines: Mantine's `styles` prop and the `styled(...)` wrappers.
 * A keyframes object only resolves in the engine that made it — each encodes its own reference as
 * an opaque marker the other silently drops or throws on. Several of these animations are used
 * from both sides, so the name is a plain string and the rules themselves are injected once by
 * {@link DS_AI_KEYFRAMES_CSS}.
 */
export const fadeIn = 'dsAiFadeIn';
export const fadeRise = 'dsAiFadeRise';
/** The one `growIn` gap in use; see {@link DS_AI_KEYFRAMES_CSS}. */
export const stageGrowIn = 'dsAiStageGrowIn';
/** The gap folded into {@link stageGrowIn}'s negative top margin. */
export const STAGES_GAP = '16px';
/** The design's `tickIn`: a done mark overshoots into place. */
export const tickIn = 'dsAiTickIn';
/** The design's `popIn`: a dialog or card rises 8px and scales .985 → 1. */
export const popIn = 'dsAiPopIn';
/** Indeterminate upload bar: fills most of the track, the settle swaps the card to "uploaded". */
export const uploadFill = 'dsAiUploadFill';
/** Counter digits slide up when the count changes. */
export const countUp = 'dsAiCountUp';
/** The design's `hlPulse`: a ring spreads 10px from the evidence highlight as it fades. */
export const hlPulse = 'dsAiHlPulse';
/** The design's `dockOpen`: a docked panel grows from nothing to its width. */
export const dockOpen = 'dsAiDockOpen';
/** The design's `dockClose`: `dockOpen` run backwards. */
export const dockClose = 'dsAiDockClose';
/** The design's `panelFloatOut`: a floating panel fades and slides 12px away as it closes. */
export const panelFloatOut = 'dsAiPanelFloatOut';
/** The design's `seqIn`: a drawer's body, replaced while open, slides in 24px from the right. */
export const seqIn = 'dsAiSeqIn';
/** A dock panel floating up into place from its rail button. */
export const floatFromRail = 'dsAiFloatFromRail';
/** The add-panel dialog dropping into place under its button. */
export const dropIn = 'dsAiDropIn';
/** The viewer's search field opening out from the toolbar. */
export const searchIn = 'dsAiSearchIn';
/** An entity row flashing once as its decision resolves. */
export const resolvedFlash = 'dsAiResolvedFlash';
/** A transaction row flashing once as it is written to the ledger. */
export const writtenFlash = 'dsAiWrittenFlash';
/** An overview bar segment growing to its share. */
export const grow = 'dsAiGrow';

const PULSE_COLOR = defaultTheme.v3.colors.orange[6];

/** Every DealStack AI keyframe, injected once by `DealstackAiMotionStyles`. */
export const DS_AI_KEYFRAMES_CSS = `
@keyframes ${fadeIn} {
	from { opacity: 0; }
	to { opacity: 1; }
}
@keyframes ${fadeRise} {
	from { opacity: 0; transform: translateY(${dsAiTravel('4px')}); }
	to { opacity: 1; transform: none; }
}
/*
 * Height 0 → auto on a \`grid-template-rows\` wrapper, with the parent's flex \`gap\` folded into a
 * negative top margin, so an item appended to a centred list grows it instead of snapping it.
 */
@keyframes ${stageGrowIn} {
	from { grid-template-rows: 0fr; margin-top: calc(-1 * ${STAGES_GAP}); opacity: 0; }
	to { grid-template-rows: 1fr; margin-top: 0; opacity: 1; }
}
@keyframes ${tickIn} {
	0% { opacity: 0; transform: scale(${dsAiScale(0.4)}); }
	60% { opacity: 1; transform: scale(${dsAiScale(1.15)}); }
	100% { opacity: 1; transform: scale(1); }
}
@keyframes ${popIn} {
	from { opacity: 0; transform: ${DS_AI_POP_FROM}; }
	to { opacity: 1; transform: none; }
}
@keyframes ${uploadFill} {
	from { transform: scaleX(0.04); }
	to { transform: scaleX(0.92); }
}
@keyframes ${countUp} {
	from { opacity: 0; transform: translateY(${dsAiTravel('40%')}); }
	to { opacity: 1; transform: none; }
}
@keyframes ${hlPulse} {
	from { box-shadow: 0 0 0 0 color-mix(in srgb, ${PULSE_COLOR} 45%, transparent); }
	to { box-shadow: 0 0 0 10px color-mix(in srgb, ${PULSE_COLOR} 0%, transparent); }
}
@keyframes ${dockOpen} {
	from { width: 0; border-left-color: transparent; }
	to { width: 360px; }
}
@keyframes ${dockClose} {
	from { width: 360px; }
	to { width: 0; border-left-color: transparent; }
}
@keyframes ${panelFloatOut} {
	from { opacity: 1; transform: none; }
	to { opacity: 0; transform: translateX(${dsAiTravel('12px')}); }
}
@keyframes ${seqIn} {
	from { opacity: 0; translate: ${dsAiTravel('24px')} 0; }
	to { opacity: 1; translate: 0 0; }
}
@keyframes ${floatFromRail} {
	from { opacity: 0; transform: translateX(${dsAiTravel('12px')}); }
	to { opacity: 1; transform: none; }
}
@keyframes ${dropIn} {
	from { opacity: 0; transform: translateY(${dsAiTravel('-4px')}); }
	to { opacity: 1; transform: none; }
}
@keyframes ${searchIn} {
	from { opacity: 0; width: calc(200px - ${dsAiTravel('200px')}); }
	to { opacity: 1; width: 200px; }
}
@keyframes ${resolvedFlash} {
	from { opacity: 1; }
	to { opacity: 0; }
}
@keyframes ${writtenFlash} {
	from { background-color: ${DS_AI_COLORS.flash}; }
}
@keyframes ${grow} {
	from { clip-path: inset(0 100% 0 0); }
	to { clip-path: inset(0 0 0 0); }
}
`;
